import { useQuery, useReactiveVar } from '@apollo/client';

import { GET_USER, GetUserQuery, GetUserQueryVariables } from '@neo.tax/shared/graphql';
import { AuthenticationState } from '@neo.tax/shared/state';

import { useUserFromAuth } from './useUserFromAuth';

export type CurrentUser = GetUserQuery['getUser'];

export type Organization = NonNullable<CurrentUser['organization']>;
export type OrganizationMember = Organization['members'][number];
export type OrganizationInvitation = Organization['invitedMembers'][number];

export const useCurrentUser = (): { user: CurrentUser | null; loading: boolean } => {
  const {
    authenticationServiceState: { authenticationUser },
    userReconciled,
  } = useReactiveVar(AuthenticationState.authenticationStateVar);

  const { loading: loadingUserId, userId } = useUserFromAuth();

  // Reconciliation ensures data in backend is in a valid state before application is used
  const userReconciliationInProgress = !userReconciled && !!authenticationUser;

  const shouldSkip = loadingUserId || !userId || userReconciliationInProgress;
  const {
    data: userData,
    error: userError,
    loading: userDataLoading,
  } = useQuery<GetUserQuery, GetUserQueryVariables>(GET_USER, shouldSkip ? { skip: true } : { variables: { userId } });

  if (userDataLoading || loadingUserId || userReconciliationInProgress) {
    return { user: null, loading: true };
  }

  if (shouldSkip) {
    return { user: null, loading: false };
  }

  if (userError) {
    throw userError;
  }

  if (!userData) {
    throw new Error('No data returned from useCurrentUser');
  }

  // returning loading: true while the reconciliation step is in progress
  return { user: userData.getUser, loading: false };
};
